/**
 * The level edits behind the object toolbar's commands that the store had no call for, each the
 * web's reading of one of the app's toolbar commands (`GUI/Dialogs/ContextToolbar.cs` builds the
 * toolbar, `GUI/Commands/MorePopupCommand.cs` its «⋯» list). They take a level and return the next
 * one, so the store commits them as one undo step and the tests call them directly.
 *
 * A project from the app lists a wall two rooms share once per room, each copy over the same
 * stretch (`src/core/geometry/openingTwins.ts` finds the doors that sit on such copies): what the
 * app does to «the wall» it does to every copy, and so do these.
 */
import type { Level, Opening, OpeningKind, Room, Surface, Vec2, Wall } from '../core/hp/types';
import { contains, MIN_WALL, projectPoint, samePoint, splitWall, validateLevel, type EditResult } from '../core/geometry/edit';
import { mirrorMovedOpenings, openingCopies } from '../core/geometry/openingTwins';
import { OPENING_SPECS } from './planConstruction';
import { t } from '../i18n/I18n';

/** Import copies agree to a centimetre or two; a point this close to a line lies on it. */
const COINCIDE = .01;
const EDGE = .001;
const near = (a: Vec2, b: Vec2, reach = COINCIDE) => Math.hypot(a.x - b.x, a.z - b.z) < reach;
const along = (wall: Wall, point: Vec2) => (point.x - wall.start.x) * wall.dir.x + (point.z - wall.start.z) * wall.dir.z;
const pointOn = (wall: Wall, offset: number): Vec2 => ({ x: wall.start.x + wall.dir.x * offset, z: wall.start.z + wall.dir.z * offset });
const round = (value: number) => Math.round(value * 1e4) / 1e4;
const onOutline = (polygon: Vec2[], p: Vec2) => polygon.some((a, i) => projectPoint(p, a, polygon[(i + 1) % polygon.length]).distance < EDGE);

/** The other rooms' copies of this very stretch of wall. */
export function wallCopies(level: Pick<Level, 'walls'>, wall: Wall): Wall[] {
  return level.walls.filter(other => other.id !== wall.id
    && ((near(other.start, wall.start) && near(other.end, wall.end)) || (near(other.start, wall.end) && near(other.end, wall.start))));
}

/** A wall along a room's outline: the app's contour wall, whose removal it asks about (RemoveWallSegment.NeedsConfirmation). */
export function isContourWall(level: Pick<Level, 'rooms'>, wall: Wall): boolean {
  return level.rooms.some(room => room.polygon.length >= 3 && [wall.start, wall.center, wall.end].every(p => onOutline(room.polygon, p)));
}

/**
 * A wall that bounds no room and meets no other wall at an end or along its body: the one kind the app lets be
 * removed or copied where a wall is picked by its face, in 3D and on the wall plan (`FurnitureCommand` enables a
 * wall only when `WallController.IsSeparateWallWithoutNeighbors`).
 */
export function isFreeStandingWall(level: Pick<Level, 'walls' | 'rooms'>, wall: Wall): boolean {
  if (isContourWall(level, wall)) return false;
  const copies = new Set(wallCopies(level, wall).map(copy => copy.id));
  return !level.walls.some(other => {
    if (other.id === wall.id || copies.has(other.id)) return false;
    const reach = Math.max(wall.thickness, other.thickness) / 2 + COINCIDE;
    return [wall.start, wall.end].some(p => projectPoint(p, other.start, other.end).distance < reach)
      || [other.start, other.end].some(p => projectPoint(p, wall.start, wall.end).distance < reach);
  });
}

/** Whether the wall stands below the level's ceiling: the app's half wall, which it moves and turns like furniture. */
export const isHalfWall = (level: Pick<Level, 'height'>, wall: Wall) => wall.height < level.height - .005;

/** «Скрыть стену» / «Показать стену» (HideShowWall): the wall and every room's copy of it. */
export function withWallHidden(level: Level, id: string, hidden: boolean): Level {
  const wall = level.walls.find(value => value.id === id);
  if (!wall) return level;
  const ids = new Set([id, ...wallCopies(level, wall).map(copy => copy.id)]);
  if (level.walls.every(value => !ids.has(value.id) || value.hidden === hidden)) return level;
  return { ...level, walls: level.walls.map(value => ids.has(value.id) && value.hidden !== hidden ? { ...value, hidden } : value) };
}

/**
 * «Разделить стену» (SplitWall): a corner where the wall was picked, the app putting it at the last touch —
 * else in the middle; each room's copy of the wall gets the same corner.
 */
export function splitWallAt(level: Level, id: string, point?: Vec2 | null): EditResult {
  const wall = level.walls.find(value => value.id === id);
  if (!wall) return { error: t('#GeometryWallNotFound') };
  if (wall.length < 2 * MIN_WALL) return { error: t('#ContextCommandsWallTooShortToSplit') };
  const at = point ? Math.min(wall.length - MIN_WALL, Math.max(MIN_WALL, along(wall, point))) : wall.length / 2;
  const corner = pointOn(wall, at);
  let result = splitWall(level, id, at);
  for (const copy of wallCopies(level, wall)) {
    if (!result.level) break;
    const piece = result.level.walls.find(value => value.id === copy.id);
    if (piece) result = splitWall(result.level, copy.id, along(piece, corner));
  }
  return result;
}

/** The app's default plinth, model 999: 15 cm tall, 1 cm deep, white (`WallSide.CreatePlinth`). */
export const DEFAULT_PLINTH: NonNullable<Wall['plinth']> = { height: .15, depth: .01, surface: { color: [1, 1, 1] } satisfies Surface };
/** The app's default ceiling molding, model 998: 10 cm tall, 1 cm deep, white (`WallSide.CreateMolding`). */
export const DEFAULT_MOLDING: NonNullable<Wall['molding']> = { height: .1, depth: .01, surface: { color: [1, 1, 1] } satisfies Surface };

/** A wall's trim: its plinth along the floor or its ceiling molding along the top. */
export type Trim = 'plinth' | 'molding';
const TRIM_DEFAULTS: Record<Trim, NonNullable<Wall['plinth']>> = { plinth: DEFAULT_PLINTH, molding: DEFAULT_MOLDING };
/** `wall` with its default trim put on, or with it taken off. */
function withTrim(wall: Wall, trim: Trim, on: boolean): Wall {
  if (on) return { ...wall, [trim]: structuredClone(TRIM_DEFAULTS[trim]) };
  const rest = { ...wall };
  delete rest[trim];
  return rest;
}

/**
 * The plinth or the ceiling molding runs along the face `normalIn` points at, the wall's own room: the only face the
 * web draws one on (the app's `WallSide.TogglePlinth`, `ToggleMolding`).
 */
export function withWallTrim(level: Level, id: string, trim: Trim, on: boolean): Level {
  const wall = level.walls.find(value => value.id === id);
  if (!wall || !!wall[trim] === on) return level;
  return { ...level, walls: level.walls.map(value => value.id !== id ? value : withTrim(value, trim, on)) };
}
export const withWallPlinth = (level: Level, id: string, on: boolean) => withWallTrim(level, id, 'plinth', on);

/** The walls whose room face is this room's floor edge: those whose plinth stands in the room. */
export function roomPlinthWalls(level: Pick<Level, 'walls' | 'rooms'>, index: number): Wall[] {
  const room = level.rooms.find(value => value.index === index);
  if (!room || room.polygon.length < 3) return [];
  return level.walls.filter(wall => !wall.hidden && facesRoom(room, wall));
}
function facesRoom(room: Room, wall: Wall): boolean {
  const inset = wall.thickness / 2 + .05;
  if (!contains(room.polygon, { x: wall.center.x + wall.normalIn.x * inset, z: wall.center.z + wall.normalIn.z * inset })) return false;
  // Along the outline, or standing inside the room as a partition.
  return [wall.start, wall.center, wall.end].every(p => onOutline(room.polygon, p) || contains(room.polygon, p));
}
/**
 * The walls whose trims go with this wall's: those of the room its face looks into (`roomPlinthWalls`), as the app
 * sets a plinth's height on every plinth of the room (`WallPlinthController.ApplyHeightToAllPlinths`) and applies a
 * material to all of them (`ApplyMaterialToPlinthOrCeilingMolding`); the wall alone when no room lies before it.
 */
export function trimRoomWalls(level: Pick<Level, 'walls' | 'rooms'>, wallId: string): Wall[] {
  const wall = level.walls.find(value => value.id === wallId);
  if (!wall) return [];
  const room = level.rooms.filter(value => value.polygon.length >= 3 && facesRoom(value, wall)).sort((a, b) => a.area - b.area)[0];
  const walls = room ? roomPlinthWalls(level, room.index) : [];
  return walls.some(value => value.id === wallId) ? walls : [wall];
}

/** A wall's face: the wall and its side, `front` the side its normal points to. */
export interface WallFace { wallId: string; side: 'front' | 'back' }

/**
 * The faces «Ко всем» of a wall's finish dresses, as the app's ApplyMaterialToAllWalls finds them
 * (`GetConnectedRoomSides`): the faces of the room this face looks into — with no room before it, the outside faces —
 * that reach this wall wall by wall through shared ends; a free-standing wall in the room that touches none of them is
 * left alone. This face comes first.
 */
export function connectedRoomFaces(level: Pick<Level, 'walls' | 'rooms'>, wallId: string, side: 'front' | 'back'): WallFace[] {
  const start = level.walls.find(value => value.id === wallId);
  if (!start) return [];
  const probe = (wall: Wall, face: 'front' | 'back') => {
    const reach = (wall.thickness / 2 + .05) * (face === 'front' ? 1 : -1);
    return { x: wall.center.x + wall.normalIn.x * reach, z: wall.center.z + wall.normalIn.z * reach };
  };
  const roomAt = (point: Vec2) => level.rooms.filter(room => room.polygon.length >= 3 && contains(room.polygon, point)).sort((a, b) => a.area - b.area)[0];
  const room = roomAt(probe(start, side));
  const faceOf = (wall: Wall): WallFace | null => {
    if (wall.hidden) return null;
    for (const face of ['front', 'back'] as const) {
      const here = roomAt(probe(wall, face));
      if (room ? here === room : !here && !!roomAt(probe(wall, face === 'front' ? 'back' : 'front'))) return { wallId: wall.id, side: face };
    }
    return null;
  };
  const faces = new Map<string, WallFace>();
  for (const wall of level.walls) { const face = wall.id === wallId ? { wallId, side } : faceOf(wall); if (face) faces.set(wall.id, face); }
  const byId = new Map(level.walls.map(wall => [wall.id, wall]));
  const touches = (a: Wall, b: Wall) => [a.start, a.end].some(p => samePoint(p, b.start) || samePoint(p, b.end));
  const reached = [start], seen = new Set([wallId]);
  for (let index = 0; index < reached.length; index++) {
    for (const id of faces.keys()) {
      const other = byId.get(id)!;
      if (!seen.has(id) && touches(reached[index], other)) { seen.add(id); reached.push(other); }
    }
  }
  return reached.map(wall => faces.get(wall.id)!);
}

export const roomHasTrims = (level: Pick<Level, 'walls' | 'rooms'>, index: number, trim: Trim) => roomPlinthWalls(level, index).some(wall => !!wall[trim]);
export const roomHasPlinths = (level: Pick<Level, 'walls' | 'rooms'>, index: number) => roomHasTrims(level, index, 'plinth');

/**
 * «Добавить плинтусы» of a floor and «Добавить молдинг» of a ceiling (TogglePlinthsInRoom): every wall of the room gets
 * one, or all go once any has one.
 */
export function withRoomTrims(level: Level, index: number, trim: Trim): Level {
  const walls = roomPlinthWalls(level, index);
  if (!walls.length) return level;
  const on = !walls.some(wall => !!wall[trim]), ids = new Set(walls.map(wall => wall.id));
  return { ...level, walls: level.walls.map(wall => !ids.has(wall.id) || !!wall[trim] === on ? wall : withTrim(wall, trim, on)) };
}
export const withRoomPlinths = (level: Level, index: number) => withRoomTrims(level, index, 'plinth');

/** The opening and the copies other rooms' copies of its wall carry of it. */
function openingGroup(level: Level, opening: Opening): Opening[] {
  const copies = new Set(openingCopies(level, opening.id));
  return [opening, ...level.openings.filter(value => copies.has(value.id))];
}

/**
 * «Дублировать» a door, window or opening (CloneFurniture on an Opening): a copy beside it on its wall, towards the
 * wall's middle first as the app shifts it (`GetDuplicateFurnitureOffsetPoint`), then the other way; the web keeps
 * openings apart, so the copy stands a whole width off instead of the app's fifth. Twins get their twin copies.
 */
export function duplicatedOpening(level: Level, id: string): { level: Level; id: string } | { error: string } {
  const opening = level.openings.find(value => value.id === id), wall = level.walls.find(value => value.id === opening?.wallId);
  if (!opening || !wall) return { error: t('#GeometryOpeningNotFound') };
  const group = openingGroup(level, opening), centre = pointOn(wall, opening.offset), step = opening.width + .1;
  const towardsMiddle = opening.offset <= wall.length / 2 ? 1 : -1;
  for (const sign of [towardsMiddle, -towardsMiddle]) {
    const target = { x: centre.x + wall.dir.x * step * sign, z: centre.z + wall.dir.z * step * sign };
    const copies: Opening[] = [];
    for (const member of group) {
      const host = level.walls.find(value => value.id === member.wallId);
      const offset = host ? along(host, target) : NaN;
      if (!host || !(offset - member.width / 2 >= .02 && offset + member.width / 2 <= host.length - .02)) break;
      copies.push({ ...structuredClone(member), id: `o_${crypto.randomUUID()}`, offset: round(offset) });
    }
    if (copies.length !== group.length) continue;
    const next = { ...level, openings: [...level.openings, ...copies] };
    // A copy of one that had no twin, set down where the next room lists the wall too, gets its twin there, as a placed one does.
    if (!validateLevel(next, level)) return { level: mirrorMovedOpenings(level, next), id: copies[group.indexOf(opening)]?.id ?? copies[0].id };
  }
  return { error: t('#ContextCommandsNoRoomForCopy') };
}

/** A hidden wall up to this long takes a door, a window or an arch across it (AddDoorToHiddenWall). */
export const HIDDEN_WALL_OPENING_MAX = 2.5;

/**
 * «Добавить дверь / окно / арку» on a hidden wall (ReplaceWallWithOpening): the wall comes back with nothing on it
 * but an opening as wide as the wall, and each room's copy of it gets the opening's twin, turned round.
 */
export function filledHiddenWall(level: Level, id: string, kind: OpeningKind): { level: Level; id: string } | { error: string } {
  const wall = level.walls.find(value => value.id === id);
  if (!wall) return { error: t('#GeometryWallNotFound') };
  const spec = OPENING_SPECS[kind], height = Math.min(spec.height, wall.height - spec.sill - .02);
  if (wall.length < .3 || height < .3) return { error: t('#GeometryOpeningDoesNotFit') };
  const copies = wallCopies(level, wall), ids = new Set([id, ...copies.map(copy => copy.id)]);
  const cleared: Level = { ...withWallHidden(level, id, false), openings: level.openings.filter(value => !ids.has(value.wallId)) };
  // The app scales its opening to the wall's length (`Scale.x = wall.Length / opening.Width`); the web's own
  // openings keep 2 cm from each end, as every opening it places does.
  const opening: Opening = { id: `o_${crypto.randomUUID()}`, wallId: id, modelRef: { kind: 'none', id: '', raw: '' },
    offset: round(wall.length / 2), width: round(wall.length - .04), height: round(height), sill: spec.sill, flipped: false, caption: kind, kind,
    ...(kind !== 'arch' ? { design: { style: kind === 'door' ? (wall.length > 1.44 ? 'double-door' as const : 'solid' as const) : 'double' as const } } : {}) };
  const twins = copies.map(copy => ({ ...structuredClone(opening), id: `o_${crypto.randomUUID()}`, wallId: copy.id, flipped: true,
    offset: round(along(copy, pointOn(wall, wall.length / 2))) }));
  const next = { ...cleared, openings: [...cleared.openings, opening, ...twins] };
  const error = validateLevel(next, level);
  return error ? { error } : { level: next, id: opening.id };
}
